import React, { useState, useEffect, useCallback } from 'react';
import { X, Package, Loader2, Camera, Trash2 } from 'lucide-react';
import PhotoActionModal from '../../PhotoActionModal';
import PhotoUploadModal, { PhotoResult } from '../../PhotoUploadModal';
import PhotoWebcamModal from '../../PhotoWebcamModal';
import { cn } from '../../../utils/cn';
import toast from 'react-hot-toast';
import Select, { SingleValue } from 'react-select';
import { createEncomenda, uploadEncomendaPhoto, CreateEncomendaRequest } from '../../../services/encomendaApi';
import { fetchWithAuth, getApiBaseUrl, condoApi } from '../../../services/api';
import { useCustomer } from '../../../contexts/CustomerContext';
import { getUnitLabel, CondoType } from '../../../utils/terminology';
import { UnitSelector, UnitOption } from '../../UnitSelector';

type SelectOption = SingleValue<{ value: string; label: string }>;

interface Person {
  id: string;
  first_name: string;
  last_name: string;
  roles?: Array<{
    role_type: string;
    is_active?: boolean;
    role_metadata?: {
      unit_info?: {
        id: string;
        number: string;
        block?: { id: string; name: string };
        street_name?: string;
        street_number?: string;
      };
    };
  }>;
}

// Resolved unit info for a person found via global search (no unit picked yet) —
// lets us auto-fill the Bloco/Unidade pickers once the porteiro selects them.
interface PersonUnitInfo {
  unitId: string;
  unitLabel: string;
  blockId?: string;
  blockLabel?: string;
}

interface EncomendaFormModalProps {
  open: boolean;
  customerId?: string;
  // Customer whose Encomendas panel this is — the caller (OperationalView) may be
  // browsing a different customer than the globally selected one (CustomerContext),
  // so these take priority over useCustomer() when provided.
  customerType?: 'condominio' | 'empresa';
  condoType?: CondoType;
  preselectedUnitId?: string;
  preselectedUnitName?: string;
  onClose: () => void;
  onSuccess: () => void;
}

const selectStyles = {
  control: (base: any, state: any) => ({
    ...base,
    backgroundColor: '#ffffff',
    borderColor: state.isFocused ? '#6b7280' : '#e5e7eb',
    boxShadow: 'none',
    minHeight: '36px',
    '&:hover': { borderColor: '#9ca3af' },
  }),
  menu: (base: any) => ({ ...base, backgroundColor: '#ffffff', border: '1px solid #e5e7eb', boxShadow: '0 4px 12px rgba(0,0,0,0.08)', zIndex: 100 }),
  option: (base: any, state: any) => ({
    ...base,
    backgroundColor: state.isFocused ? '#f3f4f6' : '#ffffff',
    color: '#111827',
    fontSize: '13px',
  }),
  singleValue: (base: any) => ({ ...base, color: '#111827', fontSize: '13px' }),
  input: (base: any) => ({ ...base, color: '#111827', fontSize: '13px' }),
  placeholder: (base: any) => ({ ...base, color: '#9ca3af', fontSize: '13px' }),
  clearIndicator: (base: any) => ({ ...base, color: '#9ca3af', '&:hover': { color: '#374151' } }),
  dropdownIndicator: (base: any) => ({ ...base, color: '#9ca3af', '&:hover': { color: '#374151' } }),
};

export default function EncomendaFormModal({
  open,
  customerId,
  customerType,
  condoType,
  preselectedUnitId,
  preselectedUnitName,
  onClose,
  onSuccess,
}: EncomendaFormModalProps) {
  const { selectedCustomer } = useCustomer();
  const isEmpresa = (customerType ?? selectedCustomer?.type) === 'empresa';

  const [selectedUnitOption, setSelectedUnitOption] = useState<UnitOption | null>(null);

  // Empresa-only: department picker
  const [departments, setDepartments] = useState<any[]>([]);
  const [selectedDepartment, setSelectedDepartment] = useState<SelectOption>(null);

  const [personOptions, setPersonOptions] = useState<{ value: string; label: string }[]>([]);
  const [selectedPerson, setSelectedPerson] = useState<SelectOption>(null);
  const [loadingPersons, setLoadingPersons] = useState(false);
  const [personSearch, setPersonSearch] = useState('');
  // Unit resolved from each searched person's resident role — used to auto-fill
  // the unit picker when a morador is picked before any unit was selected.
  const [personUnitInfoMap, setPersonUnitInfoMap] = useState<Record<string, PersonUnitInfo>>({});

  const [transportadora, setTransportadora] = useState('');
  const [trackingCode, setTrackingCode] = useState('');
  const [description, setDescription] = useState('');
  const MAX_PHOTOS = 5;
  const [photoPreviews, setPhotoPreviews] = useState<string[]>([]);
  const [photoUrls, setPhotoUrls] = useState<string[]>([]);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [showPhotoActionModal, setShowPhotoActionModal] = useState(false);
  const [showPhotoUploadModal, setShowPhotoUploadModal] = useState(false);
  const [showPhotoWebcamModal, setShowPhotoWebcamModal] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const reset = useCallback(() => {
    setSelectedUnitOption(null);
    setDepartments([]);
    setSelectedDepartment(null);
    setPersonOptions([]);
    setSelectedPerson(null);
    setPersonSearch('');
    setPersonUnitInfoMap({});
    setTransportadora('');
    setTrackingCode('');
    setDescription('');
    setPhotoPreviews([]);
    setPhotoUrls([]);
  }, []);

  // Upload a newly captured photo and append it to the strip (uploads happen
  // immediately, same as the previous single-photo flow — no encomenda ID
  // exists yet at this point, so there's nothing to attach it to server-side
  // until submit; we just collect up to MAX_PHOTOS URLs client-side).
  const handlePhotoResult = async (result: PhotoResult) => {
    if (!customerId || photoPreviews.length >= MAX_PHOTOS) return;
    setPhotoPreviews(prev => [...prev, result.base64]);
    setUploadingPhoto(true);
    try {
      const file = new File([result.blob], 'encomenda.jpg', { type: 'image/jpeg' });
      const res = await uploadEncomendaPhoto(customerId, file);
      setPhotoUrls(prev => [...prev, res.photo_url]);
    } catch {
      toast.error('Erro ao enviar foto');
      setPhotoPreviews(prev => prev.slice(0, -1));
    } finally {
      setUploadingPhoto(false);
    }
  };

  const handleRemovePhoto = (index: number) => {
    setPhotoPreviews(prev => prev.filter((_, i) => i !== index));
    setPhotoUrls(prev => prev.filter((_, i) => i !== index));
  };

  // Load departments (empresa only — condominio unit picking is handled by UnitSelector)
  useEffect(() => {
    if (!open || !customerId) return;
    reset();
    if (preselectedUnitId && preselectedUnitName) {
      setSelectedUnitOption({ value: preselectedUnitId, label: preselectedUnitName });
      return;
    }
    if (isEmpresa) {
      condoApi.getDepartments(customerId)
        .then(res => setDepartments(res.data?.data || res.data || []))
        .catch(() => {});
    }
  }, [open, customerId, preselectedUnitId, preselectedUnitName, isEmpresa, reset]);

  // Search people when selection changes or search term changes.
  // Condominio: if a unit is picked, scope the search to it (as before);
  // otherwise search moradores across ALL units — the backend's unit_id
  // filter is optional — so a porteiro can find someone by name first and
  // let the unit get filled in from their record (see handleSelectPerson).
  useEffect(() => {
    if (!customerId) {
      setPersonOptions([]);
      return;
    }
    const unitId = selectedUnitOption?.value || preselectedUnitId;
    const timer = setTimeout(async () => {
      setLoadingPersons(true);
      try {
        const params = new URLSearchParams({ page_size: '50' });
        if (isEmpresa) {
          params.set('role_type', 'staff');
          if (selectedDepartment?.value) params.set('department_id', selectedDepartment.value);
        } else {
          params.set('role_type', 'resident');
          if (unitId) params.set('unit_id', unitId);
        }
        if (personSearch.trim()) params.set('search', personSearch.trim());
        const r = await fetchWithAuth(
          `${getApiBaseUrl()}/api/v1/customers/${customerId}/people?${params}`
        );
        if (r.ok) {
          const data = await r.json();
          const people: Person[] = data.data || [];
          setPersonOptions(people.map(p => ({
            value: p.id,
            label: `${p.first_name} ${p.last_name}`,
          })));

          if (!isEmpresa) {
            const infoUpdates: Record<string, PersonUnitInfo> = {};
            people.forEach(p => {
              const residentRole = (p.roles || []).find(r => r.role_type === 'resident' && r.is_active !== false);
              const unitInfo = residentRole?.role_metadata?.unit_info;
              if (!unitInfo?.id) return;
              const unitLabel = unitInfo.street_name
                ? `${unitInfo.street_name}${unitInfo.street_number ? `, ${unitInfo.street_number}` : ''}`
                : (unitInfo.block?.name ? `${unitInfo.block.name} / ${unitInfo.number}` : unitInfo.number);
              infoUpdates[p.id] = {
                unitId: unitInfo.id,
                unitLabel,
                blockId: unitInfo.block?.id,
                blockLabel: unitInfo.block?.name,
              };
            });
            if (Object.keys(infoUpdates).length > 0) {
              setPersonUnitInfoMap(prev => ({ ...prev, ...infoUpdates }));
            }
          }
        }
      } catch {
        // ignore
      } finally {
        setLoadingPersons(false);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [selectedUnitOption, selectedDepartment, preselectedUnitId, personSearch, customerId, isEmpresa]);

  // Picking a morador before any unit was chosen auto-fills the unit picker
  // from their record (funcionário/empresa has no equivalent — departments
  // aren't per-person the same way).
  const handleSelectPerson = (opt: SelectOption) => {
    setSelectedPerson(opt);
    if (isEmpresa || selectedUnitOption || preselectedUnitId || !opt) return;
    const info = personUnitInfoMap[opt.value];
    if (!info) return;
    setSelectedUnitOption({ value: info.unitId, label: info.unitLabel, blockName: info.blockLabel });
  };

const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isEmpresa && !selectedDepartment) {
      toast.error('Selecione o departamento destinatário');
      return;
    }
    if (!isEmpresa && !selectedUnitOption) {
      toast.error('Selecione a unidade destinatária');
      return;
    }
    if (photoUrls.length === 0) {
      toast.error('Adicione ao menos uma foto da encomenda');
      return;
    }
    if (!customerId) return;
    setSubmitting(true);
    try {
      const payload: CreateEncomendaRequest = {
        unit_id: isEmpresa ? undefined : (selectedUnitOption?.value || preselectedUnitId),
        department_id: isEmpresa ? selectedDepartment?.value : undefined,
        person_id: selectedPerson?.value,
        transportadora: transportadora.trim() || undefined,
        tracking_code: trackingCode.trim() || undefined,
        description: description.trim() || undefined,
        photo_url: photoUrls[0] || undefined,
        photo_url_2: photoUrls[1] || undefined,
        photo_url_3: photoUrls[2] || undefined,
        photo_url_4: photoUrls[3] || undefined,
        photo_url_5: photoUrls[4] || undefined,
      };
      await createEncomenda(customerId, payload);
      toast.success('Encomenda registrada!');
      onSuccess();
      onClose();
    } catch (err: any) {
      toast.error(err?.message || 'Erro ao registrar encomenda');
    } finally {
      setSubmitting(false);
    }
  };

  if (!open) return null;

  const departmentOptions = departments.map((d: any) => ({ value: d.id, label: d.name }));
  // Always selectable now — condominio can search moradores across all units
  // (auto-fills Bloco/Unidade on pick) even before choosing a unit.

  return (
    <div className="fixed inset-0 z-[55] flex items-center justify-center">
      <div className="absolute inset-0 bg-black/60" onClick={onClose} />

      <div className="relative w-full max-w-md bg-white border border-gray-200 rounded-xl shadow-xl p-5 mx-4">
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Package className="h-5 w-5 text-gray-500" />
            <h2 className="text-gray-900 font-semibold text-sm">Registrar Encomenda</h2>
          </div>
          <button onClick={onClose} className="p-1.5 text-gray-400 hover:text-gray-700 rounded transition-colors">
            <X className="h-4 w-4" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-3">
          {/* Empresa: single department picker */}
          {isEmpresa && !preselectedUnitId && (
            <div>
              <label className="block text-xs text-gray-500 font-medium mb-1">Departamento <span className="text-red-400">*</span></label>
              <Select
                options={departmentOptions}
                value={selectedDepartment}
                onChange={opt => { setSelectedDepartment(opt); setSelectedPerson(null); setPersonSearch(''); }}
                placeholder="Selecione o departamento..."
                isClearable
                styles={selectStyles}
              />
            </div>
          )}

          {/* Condominio: Unit selector — hidden when unit is preselected. Shows
              Rua/Número + Quadra/Lote or Bloco/Unidade depending on the condo's layout. */}
          {!isEmpresa && !preselectedUnitId && (
            <div>
              <label className="block text-xs text-gray-500 font-medium mb-1">
                {getUnitLabel(condoType)} <span className="text-red-400">*</span>
              </label>
              <UnitSelector
                customerId={customerId || ''}
                value={selectedUnitOption}
                onChange={opt => { setSelectedUnitOption(opt); setSelectedPerson(null); setPersonSearch(''); }}
                condoType={condoType}
              />
            </div>
          )}

          {/* Preselected unit label */}
          {preselectedUnitId && (
            <div>
              <label className="block text-xs text-gray-500 font-medium mb-1">Unidade</label>
              <div className="px-3 py-2 bg-gray-50 border border-gray-200 rounded text-sm text-gray-700">
                {preselectedUnitName}
              </div>
            </div>
          )}

          {/* Recipient */}
          <div>
            <label className="block text-xs text-gray-500 font-medium mb-1">Destinatário (opcional)</label>
            <Select
              options={personOptions}
              value={selectedPerson}
              onChange={handleSelectPerson}
              onInputChange={val => setPersonSearch(val)}
              placeholder={isEmpresa ? 'Buscar colaborador...' : 'Buscar morador (todas as unidades)...'}
              isLoading={loadingPersons}
              isClearable
              filterOption={() => true}
              noOptionsMessage={() => personSearch ? 'Nenhum resultado encontrado' : 'Digite para buscar'}
              styles={selectStyles}
            />
          </div>

          {/* Transportadora */}
          <div>
            <label className="block text-xs text-gray-500 font-medium mb-1">Transportadora</label>
            <input
              type="text"
              value={transportadora}
              onChange={e => setTransportadora(e.target.value)}
              placeholder="Ex: Correios, Mercado Envios..."
              className="w-full bg-white border border-gray-200 rounded px-3 py-2 text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:border-gray-400 transition-colors"
            />
          </div>

          {/* Tracking code */}
          <div>
            <label className="block text-xs text-gray-500 font-medium mb-1">Código de rastreio</label>
            <input
              type="text"
              value={trackingCode}
              onChange={e => setTrackingCode(e.target.value)}
              placeholder="Ex: BR123456789BR"
              className="w-full bg-white border border-gray-200 rounded px-3 py-2 text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:border-gray-400 font-mono transition-colors"
            />
          </div>

          {/* Description */}
          <div>
            <label className="block text-xs text-gray-500 font-medium mb-1">Descrição</label>
            <textarea
              value={description}
              onChange={e => setDescription(e.target.value)}
              placeholder="Observações sobre a encomenda..."
              rows={2}
              className="w-full bg-white border border-gray-200 rounded px-3 py-2 text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:border-gray-400 resize-none transition-colors"
            />
          </div>

          {/* Photos — up to MAX_PHOTOS; the "add" button is simply not shown once the limit is reached */}
          <div>
            <label className="block text-xs text-gray-500 font-medium mb-1">Fotos da encomenda <span className="text-red-400">*</span></label>
            <div className="flex flex-wrap gap-2">
              {photoPreviews.map((preview, i) => (
                <div key={i} className="relative rounded-lg overflow-hidden border border-gray-200 w-16 h-16 shrink-0">
                  <img src={preview} alt={`foto ${i + 1}`} className="w-full h-full object-cover" />
                  {uploadingPhoto && i === photoPreviews.length - 1 && (
                    <div className="absolute inset-0 flex items-center justify-center bg-black/50">
                      <Loader2 className="h-4 w-4 animate-spin text-white" />
                    </div>
                  )}
                  <button
                    type="button"
                    onClick={() => handleRemovePhoto(i)}
                    disabled={uploadingPhoto}
                    className="absolute top-0.5 right-0.5 p-0.5 bg-black/60 hover:bg-black/80 rounded text-white transition-colors disabled:opacity-50"
                  >
                    <Trash2 className="h-3 w-3" />
                  </button>
                </div>
              ))}
              {photoPreviews.length < MAX_PHOTOS ? (
                <button
                  type="button"
                  onClick={() => setShowPhotoActionModal(true)}
                  disabled={uploadingPhoto}
                  className="w-16 h-16 shrink-0 flex flex-col items-center justify-center gap-1 bg-gray-50 hover:bg-gray-100 border border-dashed border-gray-300 rounded-lg text-gray-500 hover:text-gray-700 text-[10px] transition-colors disabled:opacity-50"
                >
                  <Camera className="h-4 w-4" />
                  Adicionar
                </button>
              ) : (
                <div className="w-16 h-16 shrink-0 flex items-center justify-center text-center px-1 text-[10px] text-gray-400 bg-gray-50 border border-gray-200 rounded-lg">
                  Máximo de {MAX_PHOTOS} fotos atingido
                </div>
              )}
            </div>
          </div>

          {/* Actions */}
          <div className="flex gap-2 pt-1">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 py-2 rounded text-sm font-semibold bg-white hover:bg-gray-50 text-gray-600 border border-gray-200 transition-colors"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={submitting || uploadingPhoto || photoUrls.length === 0 || (isEmpresa ? !selectedDepartment : (!selectedUnitOption && !preselectedUnitId))}
              className={cn(
                'flex-1 flex items-center justify-center gap-2 py-2 rounded text-sm font-semibold transition-colors',
                (photoUrls.length > 0 && (isEmpresa ? selectedDepartment : (selectedUnitOption || preselectedUnitId)))
                  ? 'bg-gray-900 hover:bg-gray-800 text-white'
                  : 'bg-gray-100 text-gray-400 border border-gray-200 cursor-not-allowed'
              )}
            >
              {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Package className="h-4 w-4" />}
              Registrar
            </button>
          </div>
        </form>
      </div>

      <PhotoActionModal
        open={showPhotoActionModal}
        onClose={() => setShowPhotoActionModal(false)}
        onUpload={() => { setShowPhotoActionModal(false); setShowPhotoUploadModal(true); }}
        onWebcam={() => { setShowPhotoActionModal(false); setShowPhotoWebcamModal(true); }}
        onIPCamera={() => {}}
        title="Foto da Encomenda"
        darkMode
        hideIPCamera
      />
      <PhotoUploadModal
        open={showPhotoUploadModal}
        onClose={() => setShowPhotoUploadModal(false)}
        title="Foto da Encomenda"
        cropShape="rect"
        aspectRatio={4 / 3}
        darkMode
        disableFaceDetection
        onConfirm={async (result: PhotoResult) => { setShowPhotoUploadModal(false); await handlePhotoResult(result); }}
      />
      <PhotoWebcamModal
        open={showPhotoWebcamModal}
        onClose={() => setShowPhotoWebcamModal(false)}
        title="Foto da Encomenda"
        cropShape="rect"
        aspectRatio={4 / 3}
        darkMode
        disableFaceDetection
        onConfirm={async (result: PhotoResult) => { setShowPhotoWebcamModal(false); await handlePhotoResult(result); }}
      />
    </div>
  );
}
